/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/

=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import axios from 'axios';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';

// Chakra imports
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  Image,
  Center,
  Checkbox,
  useToast,
  Icon,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  Tooltip,
} from '@chakra-ui/react';
import Card from 'components/card/Card';

import { useAuth } from 'contexts/AuthContext';
import { getBaseUrl } from 'utils/url';
import HubSpotLogo from 'assets/img/avatars/hubspot-logo.png';
import { FaCamera } from 'react-icons/fa';
import AddDevice from 'views/admin/dashboards/smartHome/components/AddDevice';
import { SubscriptionManagement } from 'components/stripe';
import FirebaseAnalyticsService from 'services/firebaseAnalyticsService';
import { ORG_ADMIN_ONLY_TOOLTIP } from 'services/firebaseAccountLookup';
import {
  MAX_TARGET_GEOGRAPHIES,
  normalizeGeographies,
  TARGET_GEOGRAPHIES,
} from 'constants/icp';

const CompanyOverview = () => {
  const [logo, setLogo] = useState<string | ArrayBuffer | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    headquartersLocation: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { token } = useAuth();
  const toast = useToast();

  const countries = useMemo(
    () => [
      'United States',
      'Canada',
      'United Kingdom',
      'Germany',
      'France',
      'Italy',
      'Spain',
      'Netherlands',
      'Belgium',
      'Switzerland',
      'Austria',
      'Sweden',
      'Norway',
      'Denmark',
      'Finland',
      'Poland',
      'Czech Republic',
      'Hungary',
      'Slovakia',
      'Slovenia',
      'Croatia',
      'Serbia',
      'Bulgaria',
      'Romania',
      'Greece',
      'Portugal',
      'Ireland',
      'Iceland',
      'Luxembourg',
      'Liechtenstein',
      'Monaco',
      'Andorra',
      'San Marino',
      'Vatican City',
      'Malta',
      'Cyprus',
      'Estonia',
      'Latvia',
      'Lithuania',
      'Australia',
      'New Zealand',
      'Japan',
      'South Korea',
      'China',
      'India',
      'Singapore',
      'Malaysia',
      'Thailand',
      'Vietnam',
      'Philippines',
      'Indonesia',
      'Taiwan',
      'Hong Kong',
      'Macau',
      'Brazil',
      'Argentina',
      'Chile',
      'Colombia',
      'Peru',
      'Venezuela',
      'Ecuador',
      'Bolivia',
      'Paraguay',
      'Uruguay',
      'Guyana',
      'Suriname',
      'French Guiana',
      'Mexico',
      'Guatemala',
      'Belize',
      'El Salvador',
      'Honduras',
      'Nicaragua',
      'Costa Rica',
      'Panama',
      'Cuba',
      'Jamaica',
      'Haiti',
      'Dominican Republic',
      'Puerto Rico',
      'Trinidad and Tobago',
      'Barbados',
      'Bahamas',
      'Grenada',
      'Saint Vincent and the Grenadines',
      'Saint Lucia',
      'Antigua and Barbuda',
      'Saint Kitts and Nevis',
      'Dominica',
      'South Africa',
      'Nigeria',
      'Kenya',
      'Ghana',
      'Ethiopia',
      'Tanzania',
      'Uganda',
      'Morocco',
      'Algeria',
      'Tunisia',
      'Libya',
      'Egypt',
      'Sudan',
      'South Sudan',
      'Chad',
      'Niger',
      'Mali',
      'Burkina Faso',
      'Senegal',
      'Guinea',
      'Sierra Leone',
      'Liberia',
      'Ivory Coast',
      'Guinea-Bissau',
      'Cape Verde',
      'The Gambia',
      'Mauritania',
      'Cameroon',
      'Central African Republic',
      'Equatorial Guinea',
      'Gabon',
      'Republic of the Congo',
      'Democratic Republic of the Congo',
      'Angola',
      'Zambia',
      'Zimbabwe',
      'Botswana',
      'Namibia',
      'Lesotho',
      'Eswatini',
      'Madagascar',
      'Mauritius',
      'Seychelles',
      'Comoros',
      'Djibouti',
      'Somalia',
      'Eritrea',
      'Burundi',
      'Rwanda',
      'Malawi',
      'Mozambique',
      'Israel',
      'Palestine',
      'Jordan',
      'Lebanon',
      'Syria',
      'Iraq',
      'Iran',
      'Kuwait',
      'Saudi Arabia',
      'Yemen',
      'Oman',
      'United Arab Emirates',
      'Qatar',
      'Bahrain',
      'Turkey',
      'Georgia',
      'Armenia',
      'Azerbaijan',
      'Kazakhstan',
      'Uzbekistan',
      'Turkmenistan',
      'Kyrgyzstan',
      'Tajikistan',
      'Afghanistan',
      'Pakistan',
      'Bangladesh',
      'Sri Lanka',
      'Maldives',
      'Nepal',
      'Bhutan',
      'Myanmar',
      'Laos',
      'Cambodia',
      'Brunei',
      'East Timor',
      'Papua New Guinea',
      'Fiji',
      'Vanuatu',
      'New Caledonia',
      'Solomon Islands',
      'Kiribati',
      'Tuvalu',
      'Nauru',
      'Palau',
      'Micronesia',
      'Marshall Islands',
      'Samoa',
      'Tonga',
      'Cook Islands',
      'Niue',
      'Tokelau',
      'American Samoa',
      'Guam',
      'Northern Mariana Islands',
      'French Polynesia',
      'Wallis and Futuna',
      'Pitcairn Islands',
      'Easter Island',
      'Galápagos Islands',
      'Falkland Islands',
      'Greenland',
      'Faroe Islands',
      'Svalbard and Jan Mayen',
      'Bouvet Island',
      'Heard Island and McDonald Islands',
      'French Southern Territories',
      'South Georgia and the South Sandwich Islands',
      'Antarctica',
      'Other',
    ],
    [],
  );

  const industries = useMemo(
    () => [
      'Technology',
      'Healthcare',
      'Financial Services',
      'Manufacturing',
      'Retail',
      'Education',
      'Real Estate',
      'Consulting',
      'Media & Entertainment',
      'Transportation',
      'Energy',
      'Government',
      'Non-Profit',
      'Agriculture',
      'Construction',
      'Hospitality',
      'Legal',
      'Insurance',
      'Telecommunications',
      'Other',
    ],
    [],
  );

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPEG, PNG, or GIF file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: 'Please upload a file smaller than 5MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogo(e.target?.result || null);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const fetchCompanyOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${getBaseUrl()}/api/accounts/company-overview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setFormData(response.data);

      // Set logo if it exists in the response
      if (response.data.logoUrl) {
        setLogo(response.data.logoUrl);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to load company overview',
      );
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const formDataToSend = new FormData();

      // Add form fields
      formDataToSend.append('companyName', formData.companyName);
      formDataToSend.append('industry', formData.industry);
      formDataToSend.append(
        'headquartersLocation',
        formData.headquartersLocation,
      );

      // Add logo file if uploaded
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      }

      await axios.post(
        `${getBaseUrl()}/api/accounts/company-overview`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      FirebaseAnalyticsService.trackFeatureUsage(
        'HubSpot Settings',
        'save_company_overview_success',
      );
      toast({
        title: 'Company overview updated successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        variant: 'solid',
      });

      // Clear the file after successful upload
      setLogoFile(null);
    } catch (err: any) {
      console.error('Error updating company overview:', err);
      FirebaseAnalyticsService.trackFeatureUsage(
        'HubSpot Settings',
        'save_company_overview_failed',
      );
      setError(
        err.response?.data?.message || 'Failed to update company overview',
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchCompanyOverview();
    // Initialize filtered countries with all countries
    setFilteredCountries(countries);
  }, [fetchCompanyOverview, countries]);

  if (isLoading) {
    return (
      <Box bg="white" borderRadius="30px" p={8} boxShadow="md">
        <Center h="200px">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="brand.400"
            size="xl"
          />
        </Center>
      </Box>
    );
  }

  return (
    <Box bg="white" borderRadius="30px" p={8} boxShadow="md">
      <Text fontSize="2xl" fontWeight="bold" mb={1}>
        Company Overview
      </Text>
      <Text color="gray.400" mb={6}>
        Help others understand your company at a glance.
      </Text>

      {error && (
        <Box
          mb={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          border="1px solid"
          borderColor="red.200"
        >
          <Text color="red.600" fontSize="sm">
            {error}
          </Text>
        </Box>
      )}

      <Flex justify="flex-start" mb={6}>
        <Box position="relative" minW="90px" minH="90px">
          <Box
            w="90px"
            h="90px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            cursor="pointer"
            onClick={handleLogoClick}
            transition="all 0.2s"
            position="relative"
            _hover={{
              boxShadow: 'lg',
            }}
          >
            {logo ? (
              <>
                <Image
                  src={logo as string}
                  w="90px"
                  h="90px"
                  objectFit="contain"
                />
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bg="rgba(0, 0, 0, 0.5)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  opacity={0}
                  transition="opacity 0.2s"
                  _hover={{ opacity: 1 }}
                >
                  <Icon as={FaCamera} w="24px" h="24px" color="white" />
                </Box>
              </>
            ) : (
              <Box
                w="90px"
                h="90px"
                borderRadius="full"
                bg="gray.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
                title="Click to upload company logo"
              >
                <Icon as={FaCamera} w="24px" h="24px" color="gray.400" />
              </Box>
            )}
          </Box>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            display="none"
          />
        </Box>
      </Flex>
      <Box mb={4}>
        <Text fontWeight="semibold" mb={1}>
          Company Name
        </Text>
        <input
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          placeholder="e.g., YourCompany Inc."
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
          }}
        />
      </Box>
      <Flex gap={4} mb={6}>
        <Box flex={1}>
          <Text fontWeight="semibold" mb={1}>
            Industry
          </Text>
          <Box position="relative">
            <Box position="relative">
              <input
                value={formData.industry}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange('industry', value);
                }}
                onFocus={() => setIsIndustryDropdownOpen(true)}
                onBlur={() => {
                  // Delay closing to allow clicking on dropdown items
                  setTimeout(() => setIsIndustryDropdownOpen(false), 200);
                }}
                placeholder="Select industry"
                style={{
                  width: '100%',
                  padding: '10px',
                  paddingRight: '40px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                }}
              />
              <Box
                position="absolute"
                right="12px"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </Box>
            </Box>
            {isIndustryDropdownOpen && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                bg="white"
                border="1px solid #E2E8F0"
                borderRadius="8px"
                maxH="200px"
                overflowY="auto"
                zIndex={1000}
                boxShadow="lg"
                mt={1}
              >
                {industries.map((industry) => (
                  <Box
                    key={industry}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    onClick={() => {
                      handleInputChange('industry', industry);
                      setIsIndustryDropdownOpen(false);
                    }}
                  >
                    <Text>{industry}</Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Box flex={1}>
          <Text fontWeight="semibold" mb={1}>
            Headquarters Location
          </Text>
          <Box position="relative">
            <Box position="relative">
              <input
                value={formData.headquartersLocation}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange('headquartersLocation', value);
                  // Filter countries based on input
                  const filtered = countries.filter((country) =>
                    country.toLowerCase().includes(value.toLowerCase()),
                  );
                  setFilteredCountries(filtered);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                onBlur={() => {
                  // Delay closing to allow clicking on dropdown items
                  setTimeout(() => setIsDropdownOpen(false), 200);
                }}
                placeholder="Search for a country..."
                style={{
                  width: '100%',
                  padding: '10px',
                  paddingRight: '40px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                }}
              />
              <Box
                position="absolute"
                right="12px"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </Box>
            </Box>
            {isDropdownOpen && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                bg="white"
                border="1px solid #E2E8F0"
                borderRadius="8px"
                maxH="200px"
                overflowY="auto"
                zIndex={1000}
                boxShadow="lg"
                mt={1}
              >
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <Box
                      key={country}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => {
                        handleInputChange('headquartersLocation', country);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <Text>{country}</Text>
                    </Box>
                  ))
                ) : (
                  <Box px={3} py={2}>
                    <Text color="gray.500">No countries found</Text>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Flex>
      <Flex justify="flex-end">
        <Button
          bg="brand.400"
          _hover={{ bg: 'brand.300' }}
          color="black"
          borderRadius="full"
          h="34px"
          px={8}
          isLoading={isSaving}
          loadingText="Saving..."
          onClick={handleSaveChanges}
        >
          Save Changes
        </Button>
      </Flex>
    </Box>
  );
};

const IdealCustomerProfile = () => {
  const [checked, setChecked] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();
  const toast = useToast();

  const handleCheck = (key: string) =>
    setChecked((c) => ({ ...c, [key]: !c[key] }));

  const selectedGeographyCount = TARGET_GEOGRAPHIES.filter(
    (geo) => checked[geo],
  ).length;

  const handleGeographyCheck = (key: string) => {
    const isChecked = !!checked[key];
    if (!isChecked && selectedGeographyCount >= MAX_TARGET_GEOGRAPHIES) {
      return;
    }
    handleCheck(key);
  };

  const fetchICPData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${getBaseUrl()}/api/accounts/icp`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Convert arrays to checkbox state
      const icpData = response.data;

      const newCheckedState: { [key: string]: boolean } = {};

      // Set target industries
      if (
        icpData.icpTargetIndustries &&
        Array.isArray(icpData.icpTargetIndustries)
      ) {
        icpData.icpTargetIndustries.forEach((industry: string) => {
          newCheckedState[industry] = true;
        });
      }

      // Set target company sizes
      if (
        icpData.icpTargetCompanySizes &&
        Array.isArray(icpData.icpTargetCompanySizes)
      ) {
        icpData.icpTargetCompanySizes.forEach((size: string) => {
          newCheckedState[size] = true;
        });
      }

      // Set target geographies (canonical values only)
      normalizeGeographies(icpData.icpTargetGeographies).forEach((geo) => {
        newCheckedState[geo] = true;
      });

      setChecked(newCheckedState);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ICP data');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Convert checkbox state to arrays
      const checkedItems = Object.keys(checked).filter((key) => checked[key]);

      const targetIndustries = [
        'Business Services',
        'Technology & Software',
        'Healthcare & Life Sciences',
        'Commerce & Consumer',
        'Industrial & Manufacturing',
        'Public & Education',
      ].filter((industry) => checkedItems.includes(industry));

      const targetCompanySizes = [
        'Micro (1–10 employees)',
        'Small (11–50)',
        'Medium (51–200)',
        'Large (201–1000)',
        'Enterprise (1000+)',
      ].filter((size) => checkedItems.includes(size));

      const targetGeographies = TARGET_GEOGRAPHIES.filter((geo) =>
        checkedItems.includes(geo),
      );

      if (targetGeographies.length === 0) {
        setError('Select at least one target geography.');
        setIsSaving(false);
        return;
      }

      await axios.post(
        `${getBaseUrl()}/api/accounts/icp`,
        {
          icpTargetIndustries: targetIndustries,
          icpTargetCompanySizes: targetCompanySizes,
          icpTargetGeographies: targetGeographies,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      FirebaseAnalyticsService.trackFeatureUsage(
        'HubSpot Settings',
        'save_icp_success',
      );
      toast({
        title: 'ICP settings updated successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        variant: 'solid',
      });
    } catch (err: any) {
      console.error('Error updating ICP data:', err);
      FirebaseAnalyticsService.trackFeatureUsage(
        'HubSpot Settings',
        'save_icp_failed',
      );
      setError(err.response?.data?.message || 'Failed to update ICP data');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchICPData();
  }, [fetchICPData]);

  if (isLoading) {
    return (
      <Box bg="white" borderRadius="30px" p={8} boxShadow="md">
        <Center h="200px">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="brand.400"
            size="xl"
          />
        </Center>
      </Box>
    );
  }

  return (
    <Box bg="white" borderRadius="30px" p={8} boxShadow="md">
      <Text fontSize="2xl" fontWeight="bold" mb={1}>
        Ideal Customer Profile (ICP)
      </Text>
      <Text color="gray.400" mb={6}>
        What kind of customers are the best fit for your product or service?
      </Text>

      {error && (
        <Box
          mb={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          border="1px solid"
          borderColor="red.200"
        >
          <Text color="red.600" fontSize="sm">
            {error}
          </Text>
        </Box>
      )}

      <Flex gap={8} flexWrap="wrap">
        <Box minW="220px" flex={1}>
          <Text fontWeight="semibold" mb={2}>
            Target Industries
          </Text>
          {[
            'Business Services',
            'Technology & Software',
            'Healthcare & Life Sciences',
            'Commerce & Consumer',
            'Industrial & Manufacturing',
            'Public & Education',
          ].map((label) => {
            const isChecked = !!checked[label];
            return (
              <Flex align="center" mb={1} key={label}>
                <Checkbox
                  isChecked={isChecked}
                  onChange={() => handleCheck(label)}
                  mr={2}
                />
                <Text>{label}</Text>
              </Flex>
            );
          })}
        </Box>
        <Box minW="220px" flex={1}>
          <Text fontWeight="semibold" mb={2}>
            Target Company Sizes
          </Text>
          {[
            'Micro (1–10 employees)',
            'Small (11–50)',
            'Medium (51–200)',
            'Large (201–1000)',
            'Enterprise (1000+)',
          ].map((label) => {
            const isChecked = !!checked[label];
            return (
              <Flex align="center" mb={1} key={label}>
                <Checkbox
                  isChecked={isChecked}
                  onChange={() => handleCheck(label)}
                  mr={2}
                />
                <Text>{label}</Text>
              </Flex>
            );
          })}
        </Box>
        <Box minW="220px" flex={1}>
          <Text fontWeight="semibold" mb={2}>
            Target Geographies
          </Text>
          <Text color="gray.500" fontSize="sm" mb={2}>
            Select up to {MAX_TARGET_GEOGRAPHIES}
          </Text>
          {TARGET_GEOGRAPHIES.map((label) => {
            const isChecked = !!checked[label];
            const atMax =
              !isChecked &&
              selectedGeographyCount >= MAX_TARGET_GEOGRAPHIES;
            return (
              <Flex align="center" mb={1} key={label}>
                <Checkbox
                  isChecked={isChecked}
                  isDisabled={atMax}
                  onChange={() => handleGeographyCheck(label)}
                  mr={2}
                />
                <Text color={atMax ? 'gray.400' : undefined}>{label}</Text>
              </Flex>
            );
          })}
        </Box>
      </Flex>
      <Flex justify="flex-end" mt={6}>
        <Button
          bg="brand.400"
          _hover={{ bg: 'brand.300' }}
          color="black"
          h="34px"
          borderRadius="full"
          px={8}
          isLoading={isSaving}
          loadingText="Saving..."
          onClick={handleSaveChanges}
        >
          Save Changes
        </Button>
      </Flex>
    </Box>
  );
};

const DeleteAccountCard = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const auth = useAuth();
  const { token, logout, isOrgAdmin } = auth ?? {};
  const toast = useToast();

  const handleDeleteAccount = async () => {
    if (!isOrgAdmin) {
      return;
    }
    if (confirmationText !== 'DELETE') {
      toast({
        title: 'Invalid confirmation',
        description: 'Please type "DELETE" to confirm account deletion',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(`${getBaseUrl()}/api/accounts/delete`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast({
        title: 'Account deleted successfully',
        description: 'Your account has been permanently deleted',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Logout and redirect
      logout();
    } catch (err: any) {
      console.error('Error deleting account:', err);
      toast({
        title: 'Failed to delete account',
        description:
          err.response?.data?.message ||
          'An error occurred while deleting your account',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  return (
    <>
      <Card
        p={8}
        borderRadius="30px"
        boxShadow="md"
        bgGradient="linear(to-br, white, gray.50)"
      >
        <Flex direction="column">
          <Text fontSize="2xl" fontWeight="bold" mb={1}>
            Delete Account
          </Text>
          <Text color="gray.500" mb={6} fontSize="sm">
            Permanently delete your account and all associated data. This action
            cannot be undone.
          </Text>
          <Tooltip
            label={ORG_ADMIN_ONLY_TOOLTIP}
            isDisabled={Boolean(isOrgAdmin)}
            placement="top"
            shouldWrapChildren
          >
            <Button
              bg="red.500"
              _hover={{ bg: 'red.600' }}
              color="white"
              borderRadius="full"
              h="34px"
              px={8}
              onClick={onOpen}
              isDisabled={!isOrgAdmin}
            >
              Delete Account
            </Button>
          </Tooltip>
        </Flex>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Account</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error" mb={4} alignItems="flex-start">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  Warning: This action is irreversible!
                </Text>
                <Text fontSize="sm">
                  Deleting your account will permanently remove all your data,
                  including:
                </Text>
                <Box as="ul" mt={2} ml={4}>
                  <li>Company information</li>
                  <li>HubSpot portal connections</li>
                  <li>Subscription data</li>
                  <li>All other account data</li>
                </Box>
              </Box>
            </Alert>
            <Text mb={4}>
              To confirm account deletion, please type <strong>DELETE</strong>{' '}
              in the box below:
            </Text>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type DELETE to confirm"
              borderColor="red.300"
              _focus={{ borderColor: 'red.500' }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteAccount}
              isLoading={isDeleting}
              loadingText="Deleting..."
              isDisabled={confirmationText !== 'DELETE' || !isOrgAdmin}
            >
              Delete Account
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const HubSpotPortal = ({
  accessMetadata,
  handleDisconnectPortal,
  handleConnectPortal,
  canDisconnectPortal,
}: {
  accessMetadata: any;
  handleDisconnectPortal: (portalId: string) => void;
  handleConnectPortal: () => void;
  canDisconnectPortal: boolean;
}) => {
  if (!accessMetadata || accessMetadata.length === 0) {
    return (
      <Card p="20px" borderRadius="30px" boxShadow="md">
        <Text fontSize="2xl" fontWeight="bold" mb={3}>
          HubSpot Portal
        </Text>
        <AddDevice onClick={handleConnectPortal} />
      </Card>
    );
  }

  return (
    <Card
      p={8}
      borderRadius="30px"
      boxShadow="md"
      bgGradient="linear(to-br, white, gray.50)"
      h="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box>
        <Flex align="center" mb={6} gap={3}>
          <Box
            borderRadius="full"
            p={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="md"
            backgroundImage={HubSpotLogo}
          >
            <Image src={HubSpotLogo} w="32px" h="32px" borderRadius="full" />
          </Box>
          <Text fontSize="2xl" fontWeight="bold">
            HubSpot Portal
          </Text>
        </Flex>
        <Flex
          w="100%"
          justify="flex-start"
          align="flex-start"
          gap={12}
          flexWrap="wrap"
          mb={4}
        >
          <Box>
            <Text fontWeight="semibold" fontSize="md" mb={1}>
              Portal ID
            </Text>
            <Text mb={3}>{accessMetadata[0].portalId}</Text>
            <Text fontWeight="semibold" fontSize="md" mb={1}>
              Domain
            </Text>
            <Text mb={3}>{accessMetadata[0].portalDomain}</Text>
            <Text fontWeight="semibold" fontSize="md" mb={1}>
              Email
            </Text>
            <Text mb={3}>{accessMetadata[0].email}</Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" fontSize="md" mb={1}>
              Date Connected
            </Text>
            <Text mb={3}>
              {accessMetadata[0].dateConnected
                ? new Date(accessMetadata[0].dateConnected).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    },
                  )
                : 'No connection date found'}
            </Text>
            <Text fontWeight="semibold" fontSize="md" mb={1}>
              Status
            </Text>
            <Box
              as="span"
              px={3}
              py={1}
              borderRadius="full"
              bg="green.100"
              color="green.700"
              fontWeight="bold"
              fontSize="sm"
              mb={3}
              display="inline-block"
            >
              {accessMetadata[0].status}
            </Box>
          </Box>
        </Flex>
      </Box>
      <Tooltip
        label={ORG_ADMIN_ONLY_TOOLTIP}
        isDisabled={canDisconnectPortal}
        placement="top"
        shouldWrapChildren
      >
        <Button
          bg="red.400"
          color="white"
          borderRadius="full"
          px={8}
          h="34px"
          _hover={{ bg: 'red.300' }}
          alignSelf="flex-start"
          mt={4}
          isDisabled={!canDisconnectPortal}
          onClick={() => handleDisconnectPortal(accessMetadata[0].portalId)}
        >
          Disconnect
        </Button>
      </Tooltip>
    </Card>
  );
};

export const HubSpotPortals = () => {
  const [accessMetadata, setAccessMetadata] = useState(null);
  const [accessMetadataLoading, setAccessMetadataLoading] = useState(false);
  const { token, isOrgAdmin } = useAuth() ?? {};

  const handleDisconnectPortal = useCallback(
    (portalId: string) => {
      if (!isOrgAdmin) return;
      // Track portal disconnection
      FirebaseAnalyticsService.trackFeatureUsage(
        'HubSpot Settings',
        'disconnect_portal',
        {
          portal_id: portalId,
        },
      );

      axios
        .delete(`${getBaseUrl()}/api/auth/delete-portal`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          data: {
            hubspotPortalId: portalId,
          },
        })
        .then(() => {
          setAccessMetadata(
            accessMetadata.filter((item: any) => item.hubId !== portalId),
          );

          // Track successful disconnection
          FirebaseAnalyticsService.trackFeatureUsage(
            'HubSpot Settings',
            'disconnect_portal_success',
            {
              portal_id: portalId,
            },
          );
        })
        .catch((err: Error) => {
          console.error(err);
          FirebaseAnalyticsService.trackFeatureUsage(
            'HubSpot Settings',
            'disconnect_portal_failed',
            {
              portal_id: portalId,
            },
          );
        });
    },
    [accessMetadata, isOrgAdmin, token],
  );

  const handleConnectPortal = useCallback(() => {
    // Track portal connection attempt
    FirebaseAnalyticsService.trackFeatureUsage(
      'HubSpot Settings',
      'connect_portal_initiated',
    );
    try {
      sessionStorage.setItem('hs_connect_initiated', '1');
    } catch {
      // ignore storage failures
    }

    window.location.href = `${getBaseUrl()}/oauth/connect?authToken=${token}`;
  }, [token]);

  useEffect(() => {
    // Track page view
    FirebaseAnalyticsService.trackNavigation('Settings', '/admin/settings');

    setAccessMetadataLoading(true);
    axios
      .get(`${getBaseUrl()}/api/auth/token-metadata`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then((result) => {
        setAccessMetadata([result.data]);
        setAccessMetadataLoading(false);
        try {
          if (sessionStorage.getItem('hs_connect_initiated') === '1') {
            sessionStorage.removeItem('hs_connect_initiated');
            if (result.data) {
              FirebaseAnalyticsService.trackFeatureUsage(
                'HubSpot Settings',
                'connect_portal_success',
                { portal_id: result.data.hubId },
              );
            }
          }
        } catch {
          // ignore storage failures
        }
      })
      .catch(() => {
        setAccessMetadataLoading(false);
        try {
          if (sessionStorage.getItem('hs_connect_initiated') === '1') {
            sessionStorage.removeItem('hs_connect_initiated');
            FirebaseAnalyticsService.trackFeatureUsage(
              'HubSpot Settings',
              'connect_portal_failed',
            );
          }
        } catch {
          // ignore storage failures
        }
      });
  }, [token]);

  if (accessMetadataLoading) {
    return (
      <Center w="100%" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.400"
          size="xl"
        />
      </Center>
    );
  }

  const tableData =
    (accessMetadata &&
      accessMetadata.map((item: any) => ({
        portalDomain: item.hubDomain,
        email: item.email,
        portalId: item.hubId,
        dateConnected: item.dateConnected,
        status: 'Connected',
      }))) ||
    [];

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: '1fr', md: '49% 48%' }}
      gap={6}
      mt="100px"
    >
      <CompanyOverview />
      <HubSpotPortal
        accessMetadata={tableData}
        handleDisconnectPortal={handleDisconnectPortal}
        handleConnectPortal={handleConnectPortal}
        canDisconnectPortal={Boolean(isOrgAdmin)}
      />
      <IdealCustomerProfile />
      <SubscriptionManagement onSubscriptionUpdate={() => {}} />
      <DeleteAccountCard />
    </Box>
  );
};
